// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Connectivity and latency state management.
 *
 * Tracks WebSocket connection status, round-trip latency metrics,
 * and computes a color-coded connectivity status with hysteresis
 * to prevent status flapping near threshold boundaries.
 */

import { create } from 'zustand';

import {
  computeConnectivityStatus,
  computeAverage,
  computePercentile,
  round3,
  MAX_LATENCY_SAMPLES,
} from './latencyHelpers';
import { logger } from '../lib/logger';
import type { ConnectivityStatus } from './latencyHelpers';

export interface ConnectivityStore {
  // State
  isConnected: boolean;
  currentLatency: number;
  latencyHistory: number[];
  latencyP95: number;
  connectivityStatus: ConnectivityStatus;

  // Actions
  setConnected: (connected: boolean) => void;
  updateLatency: (rtt: number) => void;
  getAverageLatency: () => number;
  getLatencyPercentile: (percentile: number) => number;
  reset: () => void;
}

const initialState = {
  isConnected: false,
  currentLatency: 0,
  latencyHistory: [] as number[],
  latencyP95: 0,
  connectivityStatus: 'green' as ConnectivityStatus,
};

export const useConnectivityStore = create<ConnectivityStore>()((set, get) => ({
  ...initialState,

  setConnected: (connected: boolean) => {
    logger.debug('websocket_connected_set', { connected });
    set({ isConnected: connected });
  },

  updateLatency: (rtt: number) => {
    set(current => {
      const roundedRtt = round3(rtt);
      const newHistory = [...current.latencyHistory, roundedRtt];
      if (newHistory.length > MAX_LATENCY_SAMPLES) {
        newHistory.shift();
      }
      const average = computeAverage(newHistory);
      const status = computeConnectivityStatus(
        average,
        current.connectivityStatus,
        current.isConnected
      );
      return {
        currentLatency: rtt,
        latencyHistory: newHistory,
        latencyP95: computePercentile(newHistory, 95),
        connectivityStatus: status,
      };
    });
  },

  getAverageLatency: () => computeAverage(get().latencyHistory),

  getLatencyPercentile: (percentile: number) =>
    percentile === 95 ? get().latencyP95 : computePercentile(get().latencyHistory, percentile),

  reset: () => set(initialState),
}));
